import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Database, Layers, GitBranch, Shield, Lock, Users, School, ChefHat, Building2, Landmark, CheckCircle, Eye, Award, Zap, Code, Server, Link2, Cpu } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PagePlatform = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Plateforme | Architecture"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="platform" lang="fr" />

      {/* Hero Platform */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Database size={16} />
            <span>Architecture & Sécurité</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span className="gradient-text">Architecture</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            <strong style={{ color: 'var(--color-lime)' }}>UN</strong> système centralisé • <strong style={{ color: 'var(--color-lime)' }}>CINQ</strong> portails spécialisés<br />
            Architecture multi-tenant • Données centralisées • Sécurité entreprise
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Système centralisé</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">5</div>
              <div className="stat-label">Portails spécialisés</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Sécurisé</div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section id="architecture" className="section architecture-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Architecture du Système</h2>
            <p className="section-subtitle">
              Une plateforme centralisée avec 5 portails interconnectés
            </p>
          </div>

          <div className="architecture-diagram">
            <div className="hub-spoke-container" data-testid="hub-spoke-diagram">
              {/* Center hub (static) */}
              <div className="architecture-center">
                <div className="center-hub">
                  <img src={require('../assets/logo-fox-neon.png')} alt="EcoLunch" className="center-hub-fox" />
                  <span>EcoLunch</span>
                  <p>Backend Centralisé</p>
                </div>
              </div>

              {/* SVG lines rotate as a group */}
              <div className="hub-spoke-orbit">
                <svg className="hub-spoke-lines" viewBox="0 0 620 620">
                  <line className="hub-spoke-line" x1="310" y1="310" x2="310" y2="120" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="491" y2="257" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="424" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="196" y2="464" />
                  <line className="hub-spoke-line" x1="310" y1="310" x2="129" y2="257" />
                </svg>
              </div>

              {/* Nodes: static pentagon, flux order */}
              <div className="hub-spoke-nodes">
                <div className="actor-node parent-node spoke-pos-0" onClick={() => navigate('/parents')} data-testid="spoke-parents">
                  <Users size={24} />
                  <h4>Parents</h4>
                  <p>Interface Famille</p>
                </div>
                <div className="actor-node caterer-node spoke-pos-1" onClick={() => navigate('/caterers')} data-testid="spoke-caterers">
                  <ChefHat size={24} />
                  <h4>Traiteurs</h4>
                  <p>Back-Office</p>
                </div>
                <div className="actor-node school-node spoke-pos-2" onClick={() => navigate('/schools')} data-testid="spoke-schools">
                  <School size={24} />
                  <h4>Écoles</h4>
                  <p>Cockpit Pilotage</p>
                </div>
                <div className="actor-node direction-node spoke-pos-3" onClick={() => navigate('/directions')} data-testid="spoke-directions">
                  <Building2 size={24} />
                  <h4>Directions</h4>
                  <p>Gouvernance</p>
                </div>
                <div className="actor-node css-node spoke-pos-4" onClick={() => navigate('/css')} data-testid="spoke-css">
                  <Landmark size={24} />
                  <h4>CSS</h4>
                  <p>Politique</p>
                </div>
              </div>
            </div>
          </div>

          <div className="architecture-features">
            <div className="arch-feature">
              <div className="arch-feature-icon"><Layers size={24} /></div>
              <h4>Un seul système</h4>
              <p>Pas de sites multiples, pas de fragmentation</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><GitBranch size={24} /></div>
              <h4>Données centralisées</h4>
              <p>Flux d'informations orchestrés en temps réel</p>
            </div>
            <div className="arch-feature">
              <div className="arch-feature-icon"><Shield size={24} /></div>
              <h4>Isolation sécurisée</h4>
              <p>Chaque acteur dans sa bulle protégée</p>
            </div>
          </div>
        </div>
      </section>

      {/* Flow Section */}
      <section className="section flow-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Flux Opérationnel</h2>
            <p className="section-subtitle">
              Comment les 5 acteurs interagissent dans le système
            </p>
          </div>

          <div className="flow-steps">
            <div className="flow-step">
              <div className="flow-number">1</div>
              <div className="flow-content">
                <Users size={28} />
                <h4>Parent commande</h4>
                <p>Un parent commande pour tous ses enfants en <strong>15 secondes</strong> via l'app</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">2</div>
              <div className="flow-content">
                <ChefHat size={28} />
                <h4>Traiteur reçoit automatiquement</h4>
                <p>La commande est <strong>automatiquement intégrée</strong> dans la feuille de production du traiteur</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">3</div>
              <div className="flow-content">
                <School size={28} />
                <h4>École voit en temps réel</h4>
                <p>L'école voit le volume ajusté sur son dashboard <strong>instantanément</strong></p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">4</div>
              <div className="flow-content">
                <Building2 size={28} />
                <h4>Direction supervise</h4>
                <p>La Direction constate la hausse de participation dans sa vue consolidée</p>
              </div>
            </div>

            <div className="flow-arrow"><ArrowRight size={32} /></div>

            <div className="flow-step">
              <div className="flow-number">5</div>
              <div className="flow-content">
                <Landmark size={28} />
                <h4>CSS pilote la politique alimentaire</h4>
                <p>Le Centre de services scolaire analyse les données consolidées et ajuste les <strong>politiques alimentaires</strong> à l'échelle du territoire</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="section security-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Sécurité & Confidentialité</h2>
            <p className="section-subtitle">
              Protection de niveau entreprise pour toutes vos données
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Architecture Multi-Tenant</h3>
              <p>Isolation complète des données entre organisations. Chaque CSS dans son propre espace sécurisé.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Lock size={32} /></div>
              <h3>Chiffrement de bout en bout</h3>
              <p>Toutes les données sensibles sont chiffrées en transit (TLS 1.3) et au repos (AES-256).</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Permissions hiérarchiques</h3>
              <p>Système RBAC (Role-Based Access Control). Chaque acteur voit uniquement ce qui le concerne.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Conformité RGPD/PIPEDA</h3>
              <p>Respect total des réglementations Québécoises et canadiennes sur la protection des données.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Server size={32} /></div>
              <h3>Hébergement Québécois</h3>
              <p>Serveurs situés au Québec. Vos données ne quittent jamais le territoire Québécois.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Certifications</h3>
              <p>Conformité SOC 2 Type II. Audits de sécurité réguliers par des tiers indépendants.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Hébergement Souverain — Tile dédiée */}
      <section className="section" data-testid="hosting-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Infrastructure Souveraine</h2>
            <p className="section-subtitle">
              Vos données restent au Québec, hébergées par un leader mondial
            </p>
          </div>

          <div className="hosting-tile" data-testid="hosting-tile">
            <div className="hosting-tile-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Server size={35} />
              </div>
              <div>
                <h3>Hébergement Souverain Québécois</h3>
                <span className="hosting-tile-badge">Infrastructure OVH Cloud</span>
              </div>
            </div>
            <div className="hosting-tile-body">
              <p>
                Infrastructure Québécoise <strong>(Montréal)</strong> hébergée par <strong>OVH Cloud</strong>, 
                leader mondial en souveraineté des données. Aucune donnée n'est transférée à l'étranger.
              </p>
              <div className="hosting-tile-divider"></div>
              <p className="hosting-tile-arch">
                Architecture multi-tenant de niveau entreprise : chaque Commission Scolaire, chaque École, 
                chaque Traiteur opère dans son propre espace sécurisé et totalement cloisonné.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="section benefits-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Stack Technologique Moderne</h2>
            <p className="section-subtitle">
              Technologies de pointe pour performance et fiabilité
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Code size={32} /></div>
              <h3>Backend Scalable</h3>
              <p>Architecture microservices avec API RESTful. Capable de gérer des milliers de requêtes simultanées.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Database size={32} /></div>
              <h3>Base de données optimisée</h3>
              <p>MongoDB pour flexibilité et performance. Réplication et backup automatiques quotidiens.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Temps réel</h3>
              <p>WebSockets pour mises à jour instantanées. Les dashboards se rafraîchissent automatiquement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Disponibilité 99.9%</h3>
              <p>SLA garanti. Infrastructure redondante avec basculement automatique en cas de panne.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intégrations & Technologies */}
      <section className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Intégrations & Technologies</h2>
            <p className="section-subtitle">
              Connectez EcoLunch à votre écosystème existant
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Link2 size={32} /></div>
              <h3>API avec Systèmes Actuels</h3>
              <p>API modulable - nous adapterons notre plateforme à votre gestion actuelle pour centraliser toutes les opérations. Tout ce qui est fait sur EcoLunch sera reflété dans chacun de vos systèmes. Intégrations disponibles : Système de gestion, Système comptable, Acomba & Acomba GO.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Cpu size={32} /></div>
              <h3>Technologie Unique</h3>
              <p>IA développée exclusivement pour la gestion de traiteur alimentaire. Algorithmes propriétaires entraînés sur des années de données du secteur. Programmée spécifiquement pour optimiser chaque aspect de vos opérations. Aucune IA générique - 100% adaptée à VOS besoins.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Code size={32} /></div>
              <h3>Architecture Évolutive</h3>
              <p>Plateforme conçue pour s'adapter à vos besoins futurs. Ajout de nouveaux modules sans interruption de service. Mise à jour continue sans impact sur vos opérations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste — Fondation locale */}
      <section className="section" data-testid="manifeste-platform">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="manifeste-tile" data-testid="manifeste-fondation-platform">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}>
                <Award size={30} />
              </div>
              <h3>Une fondation sérieuse, locale et crédible</h3>
            </div>
            <p>
              L'approche technologique et partenariale derrière EcoLunch s'appuie sur des acteurs reconnus :
            </p>
            <p>
              <strong>Sezzle Canada</strong> — paiements flexibles<br />
              <strong>Paysafe Montréal</strong> — traitement sécurisé<br />
              <strong>OVH Cloud Québec</strong> — hébergement souverain
            </p>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PagePlatform;
